import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { announcementsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';

/**
 * Bannière "Nouveautés" affichée en haut du dashboard à la prochaine connexion.
 * Une bannière à la fois — l'utilisateur DOIT cliquer sur "Découvrir" puis
 * "J'ai compris" pour la faire disparaître et voir la suivante. Pas de croix
 * de fermeture rapide : l'objectif est qu'il lise chaque nouveauté.
 * Un compteur "1/N" indique combien il en reste. Rappel : toutes les
 * nouveautés restent accessibles à tout moment depuis
 * « Outils & liens utiles → Guide des nouveautés ».
 */
export const FeatureAnnouncement = () => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const dismissLock = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        let active = true;
        announcementsAPI.current()
            .then((res) => { if (active) setItems(res.data?.announcements || []); })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    const current = items[0];
    const total = items.length;
    if (!current) return null;

    const dismiss = async () => {
        if (dismissLock.current) return;
        dismissLock.current = true; setSaving(true); setError(null);
        try { await announcementsAPI.dismiss(current.id); }
        catch {
            setError('La confirmation n’a pas pu être enregistrée. Réessayez.');
            return;
        } finally { dismissLock.current = false; setSaving(false); }
        setOpen(false);
        setItems((prev) => prev.filter((a) => a.id !== current.id));
        // Si l'annonce définit un `cta_url`, on redirige juste après le
        // dismiss — le clic CTA sert alors à la fois de « J'ai compris »
        // et de raccourci vers la fonctionnalité. Route interne uniquement.
        if (current.cta_url && typeof current.cta_url === 'string' && current.cta_url.startsWith('/')) {
            try { navigate(current.cta_url); } catch { window.location.href = current.cta_url; }
        }
    };

    return (
        <>
            <div
                data-testid="feature-announcement-banner"
                className="relative overflow-hidden rounded-xl border-2 border-purple-300 bg-gradient-to-r from-purple-50 via-fuchsia-50 to-indigo-50 px-4 py-3.5 shadow-sm"
            >
                {/* Barre latérale gauche pour bien marquer l'importance */}
                <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-purple-500 to-fuchsia-600" />
                <div className="flex items-center gap-3 pl-2">
                    <div className="p-2 rounded-full bg-purple-100 shrink-0">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span data-testid="feature-announcement-label" className="text-[10px] font-bold uppercase tracking-wider text-purple-700 bg-purple-100 rounded-full px-2 py-0.5">
                                Nouveauté à découvrir
                            </span>
                            {total > 1 && (
                                <span
                                    data-testid="feature-announcement-counter"
                                    className="text-[10px] font-semibold text-purple-700 bg-white/70 rounded-full px-2 py-0.5 border border-purple-200"
                                >
                                    1 / {total}
                                </span>
                            )}
                        </div>
                        <p data-testid="feature-announcement-title" className="text-sm font-semibold text-slate-800 mt-1 break-words">{current.title}</p>
                        <p data-testid="feature-announcement-hint" className="text-[11px] text-slate-500 mt-0.5 italic">
                            Clique sur <b>Découvrir</b> puis <b>J&apos;ai compris</b> pour passer à la suivante.
                        </p>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => { setError(null); setOpen(true); }}
                        data-testid="feature-announcement-discover"
                        className="bg-purple-600 hover:bg-purple-700 text-white shrink-0 shadow-sm"
                    >
                        Découvrir <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="feature-announcement-dialog">
                    <DialogHeader>
                        <div className="flex items-center gap-2 mb-1">
                            <span data-testid="feature-announcement-dialog-label" className="text-[10px] font-bold uppercase tracking-wider text-purple-700 bg-purple-100 rounded-full px-2 py-0.5">
                                Nouveauté
                            </span>
                            {total > 1 && (
                                <span data-testid="feature-announcement-remaining-count" className="text-[10px] font-semibold text-slate-500">
                                    {total - 1} autre{total > 2 ? 's' : ''} après celle-ci
                                </span>
                            )}
                        </div>
                        <DialogTitle data-testid="feature-announcement-dialog-title" className="flex items-center gap-2 text-lg">
                            <Sparkles className="h-5 w-5 shrink-0 text-purple-600" />
                            {current.title}
                        </DialogTitle>
                        {current.intro && (
                            <DialogDescription data-testid="feature-announcement-intro" className="text-slate-600 pt-1">
                                {current.intro}
                            </DialogDescription>
                        )}
                    </DialogHeader>
                    <ul className="space-y-3 py-2">
                        {(current.points || []).map((p, i) => (
                            <li key={i} data-testid={`feature-announcement-point-${i}`} className="flex items-start gap-2.5 text-sm text-slate-700">
                                <CheckCircle2 className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                    {error && <p role="alert" data-testid="feature-announcement-confirm-error" className="text-sm text-red-700">{error}</p>}
                    <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
                        <p data-testid="feature-announcement-catalog-hint" className="text-[11px] text-slate-500 italic order-2 sm:order-1">
                            Tu retrouveras toutes les nouveautés dans <b>Outils &amp; liens utiles → Guide des nouveautés</b>.
                        </p>
                        <Button
                            onClick={dismiss}
                            disabled={saving}
                            data-testid="feature-announcement-confirm"
                            className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto order-1 sm:order-2"
                        >
                            {saving ? 'Enregistrement…' : current.cta || "J'ai compris"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default FeatureAnnouncement;
